import React from 'react';
import {
  BookOpen,
  Award,
  Calendar,
  MapPin,
  Star,
  Scroll,
  Brain,
  Eye,
  Users,
  Globe,
  Quote,
  Sparkles,
  ShieldCheck,
  Stethoscope,
} from 'lucide-react';

const AcharyaSushruta = () => {
  const lifeTimeline = [
    {
      era: '6th Century BCE',
      title: 'Birth in Kashi',
      description:
        'Born in Kashi (modern-day Varanasi), Sushruta became a disciple of Dhanvantari, the divine physician of Ayurveda, beginning a lifelong study of medicine and surgery.',
      icon: MapPin,
    },
    {
      era: 'Formative Years',
      title: 'Mastery of Surgical Science',
      description:
        'Trained systematically in anatomy, instrumentation, and operative technique — emphasizing rigorous practice on models and fruit before ever touching a patient.',
      icon: BookOpen,
    },
    {
      era: 'Life’s Work',
      title: 'The Sushruta Samhita',
      description:
        'Authored the foundational surgical treatise: 186 chapters across six sections describing over 1,120 diseases, 700 medicinal plants, 300+ procedures, and 120+ instruments.',
      icon: Scroll,
    },
    {
      era: '1815 CE',
      title: 'The World Takes Notice',
      description:
        'British surgeon Joseph Constantine Carpue performs the first major Western plastic surgery using the "Indian method" of rhinoplasty described by Sushruta — over two millennia after it was written.',
      icon: Globe,
    },
    {
      era: 'Today',
      title: 'Living Legacy',
      description:
        'Studied in medical schools worldwide, honored as the Father of Surgery, and recognized as invaluable world heritage — his principles of asepsis and reconstruction still guide modern practice.',
      icon: Award,
    },
  ];

  const achievementCategories = [
    {
      icon: Brain,
      title: 'Surgical Innovation',
      summary: '300+ procedures and 120+ instruments, including two techniques still practiced today.',
      details: [
        {
          name: 'Rhinoplasty (Nasal Reconstruction)',
          note: 'The "Indian method" using a forehead flap — still taught in modern plastic surgery.',
        },
        {
          name: 'Cataract Surgery',
          note: 'The couching technique, performed with purpose-built instruments.',
        },
        {
          name: 'Lithotomy & Obstetric Procedures',
          note: 'Bladder stone removal, cesarean section, and embryotomy.',
        },
      ],
    },
    {
      icon: Eye,
      title: 'Ophthalmology',
      summary: 'Catalogued 76 eye diseases and performed the world’s first recorded cataract surgery.',
      details: [
        {
          name: 'Disease Classification',
          note: '76 distinct eye conditions described with diagnosis and treatment.',
        },
        {
          name: 'Couching Technique',
          note: 'A precise method for displacing the clouded lens, restoring vision.',
        },
      ],
    },
    {
      icon: Stethoscope,
      title: 'Anatomy & Wound Care',
      summary: 'Mapped the human body in detail no predecessor had attempted.',
      details: [
        {
          name: 'Human Anatomy',
          note: '300 bones, 107 joints, 900 ligaments, and 40 major blood vessels described.',
        },
        {
          name: 'Wound Classification & Suturing',
          note: 'Systematic wound categories and suturing methods, including techniques using ant heads.',
        },
      ],
    },
  ];

  const instruments = [
    { label: 'Shastra', detail: '20 types of sharp instruments — knives, scissors, lancets' },
    { label: 'Yantra', detail: '101 types of blunt instruments — forceps, probes, catheters' },
    { label: 'Specialized Tools', detail: 'Purpose-built instruments for procedures like cataract surgery' },
    { label: 'Materials', detail: 'Crafted from iron, steel, stone, and wood' },
  ];

  const principles = [
    'Patient assessment, positioning, and seasonal timing (Ritu) before surgery',
    'Ashtavidha Shastra Karma — the eight fundamental surgical procedures',
    'Precise incision technique and hemostasis (bleeding control)',
    'Wound dressing, herbal pain management, and dietary recovery protocols',
  ];

  return (
    <div className="acharya-page">
      {/* Hero */}
      <section className="acharya-hero">
        <div className="container">
          <div className="acharya-hero-content text-center">
            <span className="acharya-eyebrow">
              <Sparkles size={14} />
              Legends in Ayurvedic Surgery
            </span>
            <h1>
              <span className="sanskrit">आचार्य सुश्रुत</span>
              <br />
              Acharya Sushruta
            </h1>
            <p className="acharya-hero-subtitle">
              The Father of Surgery &middot; Pioneer of Plastic Surgery &middot; Ancient India&rsquo;s Greatest Surgeon
            </p>
            <div className="acharya-hero-meta">
              <div className="meta-item">
                <Calendar size={18} />
                <span>6th Century BCE</span>
              </div>
              <div className="meta-item">
                <MapPin size={18} />
                <span>Kashi (Varanasi), India</span>
              </div>
              <div className="meta-item">
                <BookOpen size={18} />
                <span>Author of Sushruta Samhita</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div className="acharya-intro">
            <p className="lead">
              Acharya Sushruta, often hailed as the &ldquo;Father of Surgery&rdquo; and the &ldquo;Father of Plastic
              Surgery,&rdquo; was an ancient Indian physician and surgeon whose monumental work, the{' '}
              <strong>Sushruta Samhita</strong>, remains one of the foundational texts of Ayurveda — and the pinnacle
              of surgical knowledge in the ancient world.
            </p>
            <p>
              His contributions to medicine and surgery were so profound that they continue to influence surgical
              practice more than 2,500 years later, predating comparable developments in Western medicine by
              centuries.
            </p>
          </div>
        </div>
      </section>

      {/* Life & Legacy Timeline */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header">
            <h2>A Life That Shaped Surgery</h2>
            <p>From a disciple in ancient Kashi to a name studied in operating theatres worldwide</p>
          </div>

          <div className="acharya-timeline">
            {lifeTimeline.map((item, index) => (
              <div key={index} className="acharya-timeline-item">
                <div className="acharya-timeline-marker">
                  <item.icon size={22} />
                </div>
                <div className="acharya-timeline-content">
                  <span className="acharya-timeline-era">{item.era}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="section">
        <div className="container">
          <div className="content-header">
            <h2>Where He Changed Medicine Forever</h2>
            <p>Three fields Sushruta advanced by centuries, grouped by discipline</p>
          </div>

          <div className="acharya-achievements">
            {achievementCategories.map((category, index) => (
              <div key={index} className="acharya-achievement-card card">
                <div className="acharya-achievement-header">
                  <div className="card-icon">
                    <category.icon size={28} />
                  </div>
                  <div>
                    <h3>{category.title}</h3>
                    <p className="acharya-achievement-summary">{category.summary}</p>
                  </div>
                </div>
                <ul className="acharya-achievement-details">
                  {category.details.map((detail, idx) => (
                    <li key={idx}>
                      <Star size={14} />
                      <span>
                        <strong>{detail.name}</strong>
                        <br />
                        {detail.note}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instruments & Principles */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header">
            <h2>The Surgical Method</h2>
            <p>How Sushruta equipped and disciplined the practice of surgery</p>
          </div>

          <div className="acharya-method-grid">
            <div className="acharya-method-card card">
              <div className="acharya-method-title">
                <ShieldCheck size={22} />
                <h3>Instruments — Yantra &amp; Shastra</h3>
              </div>
              <p className="acharya-method-intro">
                Sushruta classified surgical instruments into <strong>Shastra</strong> (sharp) and{' '}
                <strong>Yantra</strong> (blunt), detailing over 120 tools with remarkable engineering precision.
              </p>
              <ul className="acharya-method-list">
                {instruments.map((instrument, index) => (
                  <li key={index}>
                    <span className="acharya-method-label">{instrument.label}</span>
                    <span>{instrument.detail}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="acharya-method-card card">
              <div className="acharya-method-title">
                <Users size={22} />
                <h3>Principles &amp; Protocols</h3>
              </div>
              <p className="acharya-method-intro">
                A complete surgical philosophy — from preparation through recovery — that anticipates modern
                perioperative care.
              </p>
              <ul className="acharya-method-list acharya-method-list--plain">
                {principles.map((point, index) => (
                  <li key={index}>
                    <Star size={14} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="section">
        <div className="container">
          <div className="acharya-quote card">
            <Quote size={40} className="acharya-quote-icon" />
            <blockquote>
              <p className="sanskrit-quote">
                &ldquo;शल्यं शस्त्रकृतं व्रणं विशोध्य संशोध्य च योजयेत् |<br />
                सूत्रेण वा अग्निना वा क्षाराल्कलीभिः एव वा ||&rdquo;
              </p>
              <p className="quote-translation">
                &ldquo;After cleansing and purifying the wound caused by a sharp instrument, it should be united by
                sutures, cautery, or alkaline substances.&rdquo;
              </p>
              <cite>&mdash; Sushruta Samhita, Sutrasthana 25.3</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container">
          <div className="acharya-cta text-center">
            <h2>Explore the Sushruta Samhita</h2>
            <p>
              Dive deeper into the complete text and discover the surgical wisdom that has influenced medical
              practice for over two millennia.
            </p>
            <div className="cta-buttons">
              <a href="/sushruta-samhita" className="btn btn-primary btn-large">
                <BookOpen size={20} />
                Read Sushruta Samhita
              </a>
              <a href="/textbooks" className="btn btn-secondary btn-large">
                <Scroll size={20} />
                Access Study Materials
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcharyaSushruta;
